import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useSelector } from 'src/store/store';
import { stripBBTags } from 'src/utils/string';
import i18n from 'src/utils/translate';
import { trackFootfallEvent } from 'src/utils/footfall';
import { Event } from 'src/constants/footfall';
import { getElementHeight } from 'src/utils/elementSize';
import { fadeEllipsis } from 'styles-js/mixins/fade';
import { LinkButton } from 'styles-js/buttons';
import { text } from 'styles-js/mixins/typography';
import { useMediaQuery } from 'src/utils/mediaQuery';
const DEFAULT_HEIGHT_BIO = 105;
const DEFAULT_HEIGHT_BIO_SMALL = 84;
export default function ProfileBio() {
  const profileUser = useSelector(({
    pages
  }) => pages.profile.profileUser);
  const aboutMe = useSelector(({
    pages
  }) => pages.profile.basics.aboutMe);
  const isMobile = useMediaQuery('underScreenMedium');
  const bioHeight = isMobile ? DEFAULT_HEIGHT_BIO_SMALL : DEFAULT_HEIGHT_BIO;
  const [isSeeMoreComponentVisible, setIsSeeMoreComponentVisible] = useState(true);
  const [isBioExpanded, setIsBioExpanded] = useState(true);
  useEffect(() => {
    if (isBioExpanded) {
      const isBioLarge = getElementHeight('.profile-bio') > bioHeight + 30; // 30 = size of margin
      setIsSeeMoreComponentVisible(isBioLarge);
      setIsBioExpanded(!isBioLarge);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleBioExpanded = () => {
    if (isSeeMoreComponentVisible) {
      if (!isBioExpanded) {
        trackFootfallEvent(Event.Clicked, {
          clickType: 'profile-bio',
          clickMetadata: {
            origin: profileUser.isOwnProfile ? 'own-profile' : 'other-profile'
          }
        });
      }
      setIsBioExpanded(!isBioExpanded);
    }
  };
  const formattedAboutMe = aboutMe && stripBBTags(aboutMe);
  return <div data-sentry-component="ProfileBio" data-sentry-source-file="profileBio.tsx">
      {aboutMe ? <BioBody $height={bioHeight} $isExpanded={isBioExpanded} className={'profile-bio'} data-testid="profile__about_bio" onClick={toggleBioExpanded}>
          {formattedAboutMe}
          {isSeeMoreComponentVisible && <>
              <SeeMore $isExpanded={isBioExpanded} data-testid="profile__about_bio_see_more">
                <LinkButton>{i18n.t('Read more')}</LinkButton>
              </SeeMore>
              <>
                <br />
                <SeeLess $isExpanded={isBioExpanded} data-testid="profile__about_bio_see_less">
                  <LinkButton>{i18n.t('Read less')}</LinkButton>
                </SeeLess>
              </>
            </>}
        </BioBody> : i18n.t("Hi! I'm ") + profileUser.username}
    </div>;
}
const Expander = styled.span`
  z-index: 1;
  background-color: ${({
  theme
}) => theme.colorWhite};
  line-height: ${({
  theme
}) => theme.fontLineHeightS};
`;
const SeeLess = styled(Expander)<{
  $isExpanded?: boolean;
}>`
  position: relative;
  opacity: ${props => props.$isExpanded ? '1' : '0'};
  ${props => props.$isExpanded ? 'transition: all 200ms linear;' : 'transition: all 350ms linear;'}
`;
const SeeMore = styled(Expander)<{
  $isExpanded?: boolean;
}>`
  position: absolute;
  bottom: 0;
  right: 0;
  opacity: ${props => !props.$isExpanded ? '1' : '0'};
  ${props => !props.$isExpanded ? 'transition: opacity 300ms ease-in;' : 'transition: opacity 0s linear;'}
  ${props => !props.$isExpanded ? 'transition-delay: 650ms' : ''}
`;
const BioBody = styled.div<{
  $isExpanded?: boolean;
  $height: number;
}>`
  ${text}
  position: relative;
  overflow: hidden;
  word-wrap: break-word;
  white-space: pre-wrap;
  max-height: ${props => !props.$isExpanded ? `${props.$height}px` : `15000px`}; // we don\t have any limit for textarea bio size. seems like the DB does not impose a limit either?!
  ${props => !props.$isExpanded ? 'transition: all 0.8s linear;' : 'transition: max-height 1.5s linear;'}

  ::after{
    ${fadeEllipsis};
    display: ${props => !props.$isExpanded ? 'block' : 'none'};
    height: ${({
  theme
}) => theme.fontLineHeightS};
    width: 100%;
  }

  @media (${({
  theme
}) => theme.underScreenMedium}) {
    word-break: break-all; // word-wrap not working on phones sometimes
  }
`;